import * as i0 from '@angular/core';
import { InjectionToken, NgModule, Injectable, Inject, ErrorHandler } from '@angular/core';
import * as i1 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i2 from '@elastic/apm-rum';
import { apm } from '@elastic/apm-rum';
import { afterFrame } from '@elastic/apm-rum-core';

/**
 * MIT License
 *
 * Copyright (c) 2017-present, Elasticsearch BV
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 *
 */
const APM = new InjectionToken('APM Base Client');
let ApmModule = /*#__PURE__*/(() => {
  class ApmModule {}
  ApmModule.ɵfac = function ApmModule_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ApmModule)();
  };
  ApmModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
    type: ApmModule
  });
  ApmModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
    providers: [{
      provide: APM,
      useValue: apm
    }],
    imports: [[RouterModule]]
  });
  return ApmModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ApmService = /*#__PURE__*/(() => {
  class ApmService {
    constructor(apm, router, ngZone) {
      this.apm = apm;
      this.router = router;
      this.ngZone = ngZone;
    }
    init(config) {
      const apmInstance = this.ngZone.runOutsideAngular(() => this.apm.init(config));
      if (!apmInstance.isActive()) {
        return apmInstance;
      }
      /**
       * Start listening to route change once we
       * intiailize to set the correct transaction names
       */
      this.observe();
      return apmInstance;
    }
    observe() {
      let transaction;
      this.router.events.subscribe(event => {
        const eventName = event.toString();
        if (eventName.indexOf('NavigationStart') >= 0) {
          const name = event.url;
          transaction = this.apm.startTransaction(name, 'route-change', {
            managed: true,
            canReuse: true
          });
        } else if (eventName.indexOf('NavigationError') >= 0) {
          transaction && transaction.detectFinish();
        } else if (eventName.indexOf('NavigationEnd') >= 0) {
          if (!transaction) {
            return;
          }
          /**
           * The below logic must be placed in NavigationEnd since
           * the we depend on the current route state to get the path
           *
           * Even If there are any redirects, the router state path
           * will be matched with the correct url on navigation end
           *
           * Traverse the activated route tree to figure out the nested
           * route path
           */
          const route = this.router.routerState.root.firstChild;
          if (route) {
            let child = route;
            let path = '/' + child.routeConfig.path;
            while (child) {
              child = child.firstChild;
              if (child && child.routeConfig) {
                const currentPath = child.routeConfig.path;
                /**
                 * Ignore empty path's in the route config
                 */
                if (currentPath) {
                  path += '/' + currentPath;
                }
              }
            }
            transaction.name = path;
          }
          afterFrame(() => transaction.detectFinish());
        }
      });
    }
  }
  ApmService.ɵfac = function ApmService_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ApmService)(i0.ɵɵinject(APM), i0.ɵɵinject(i1.Router), i0.ɵɵinject(i0.NgZone));
  };
  ApmService.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ApmService,
    factory: ApmService.ɵfac,
    providedIn: 'root'
  });
  return ApmService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * MIT License
 *
 * Copyright (c) 2017-present, Elasticsearch BV
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 *
 */
let ApmErrorHandler = /*#__PURE__*/(() => {
  class ApmErrorHandler extends ErrorHandler {
    constructor(apm) {
      super();
      this.apm = apm;
    }
    handleError(error) {
      this.apm.captureError(error.originalError || error);
      super.handleError(error);
    }
  }
  ApmErrorHandler.ɵfac = function ApmErrorHandler_Factory(__ngFactoryType__) {
    return new (__ngFactoryType__ || ApmErrorHandler)(i0.ɵɵinject(APM));
  };
  ApmErrorHandler.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
    token: ApmErrorHandler,
    factory: ApmErrorHandler.ɵfac
  });
  return ApmErrorHandler;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * MIT License
 *
 * Copyright (c) 2017-present, Elasticsearch BV
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 *
 */

/**
 * Generated bundle index. Do not edit.
 */

export { APM, ApmErrorHandler, ApmModule, ApmService };
